/**
 * Low-level crypto primitives.
 *
 * Key derivation is Argon2id via hash-wasm; symmetric encryption is AES-256-GCM
 * via WebCrypto. Nothing here touches the network and nothing here persists
 * anything — that is the storage layer's job.
 */

import { argon2id } from 'hash-wasm';

export const AES_KEY_BITS = 256;
export const GCM_IV_BYTES = 12;
export const SALT_BYTES = 16;

/**
 * Argon2id parameters.
 *
 * 64 MiB and three passes is the OWASP-recommended floor and takes roughly
 * 200-400 ms on a mid-range phone. That is a deliberate cost: the login screen
 * is the only thing standing between a stolen phone and ten years of passenger
 * addresses, and the derived key is the only copy of it that exists.
 */
export const ARGON2_MEMORY_KIB = 65_536;
export const ARGON2_ITERATIONS = 3;
export const ARGON2_PARALLELISM = 1;
export const DERIVED_KEY_BYTES = 32;

export function randomBytes(length: number): Uint8Array {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return bytes;
}

export function toBase64(bytes: Uint8Array): string {
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

export function fromBase64(value: string): Uint8Array {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

export function toHex(bytes: Uint8Array): string {
  return [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
}

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

export function utf8(value: string): Uint8Array {
  return textEncoder.encode(value);
}

export function fromUtf8(bytes: Uint8Array): string {
  return textDecoder.decode(bytes);
}

/** Derive raw key material from a password. Slow by design. */
export async function deriveKeyMaterial(password: string, salt: Uint8Array): Promise<Uint8Array> {
  const hex = await argon2id({
    password,
    salt,
    parallelism: ARGON2_PARALLELISM,
    iterations: ARGON2_ITERATIONS,
    memorySize: ARGON2_MEMORY_KIB,
    hashLength: DERIVED_KEY_BYTES,
    outputType: 'hex',
  });
  const bytes = new Uint8Array(DERIVED_KEY_BYTES);
  for (let i = 0; i < DERIVED_KEY_BYTES; i += 1) {
    bytes[i] = Number.parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return bytes;
}

/**
 * Import raw bytes as a non-extractable AES-GCM key. Non-extractable means the
 * key cannot be read back out of the browser, even by our own code.
 */
export async function importAesKey(raw: Uint8Array, extractable = false): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    'raw',
    raw.slice().buffer as ArrayBuffer,
    { name: 'AES-GCM', length: AES_KEY_BITS },
    extractable,
    ['encrypt', 'decrypt'],
  );
}

export async function generateDataKey(): Promise<CryptoKey> {
  return crypto.subtle.generateKey({ name: 'AES-GCM', length: AES_KEY_BITS }, true, [
    'encrypt',
    'decrypt',
  ]);
}

export interface Sealed {
  iv: string;
  ct: string;
}

export async function seal(key: CryptoKey, plaintext: Uint8Array): Promise<Sealed> {
  const iv = randomBytes(GCM_IV_BYTES);
  const ct = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: iv.slice().buffer as ArrayBuffer },
    key,
    plaintext.slice().buffer as ArrayBuffer,
  );
  return { iv: toBase64(iv), ct: toBase64(new Uint8Array(ct)) };
}

export async function open(key: CryptoKey, sealed: Sealed): Promise<Uint8Array> {
  const iv = fromBase64(sealed.iv);
  const ct = fromBase64(sealed.ct);
  const plain = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: iv.slice().buffer as ArrayBuffer },
    key,
    ct.slice().buffer as ArrayBuffer,
  );
  return new Uint8Array(plain);
}

export async function sealJson(key: CryptoKey, value: unknown): Promise<Sealed> {
  return seal(key, utf8(JSON.stringify(value)));
}

export async function openJson<T>(key: CryptoKey, sealed: Sealed): Promise<T> {
  return JSON.parse(fromUtf8(await open(key, sealed))) as T;
}

export async function sha256Hex(input: string | Uint8Array): Promise<string> {
  const bytes = typeof input === 'string' ? utf8(input) : input;
  const digest = await crypto.subtle.digest('SHA-256', bytes.slice().buffer as ArrayBuffer);
  return toHex(new Uint8Array(digest));
}

/** Constant-time comparison, so a wrong verifier cannot be probed by timing. */
export function timingSafeEqual(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i += 1) diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  return diff === 0;
}
